/**
 * CheckinBanner
 * CCH Care — Facility check-in (per-staff presence)
 *
 * Path: src/components/layout/CheckinBanner.jsx
 *
 * Renders a strip below the header for the float/oversight cohort (licensed
 * staff). CNAs are rostered via shift_assignments and never see it.
 *
 * Two states:
 *   - Not checked in: a prompt with a facility control. One facility → a
 *     single "Check in to X" button; multiple → a picker + Check in button.
 *   - Checked in: confirmation ("📍 Checked in: X") with a Check out button.
 *
 * Teal theme (matches --color-primary #1B6B93) so it reads as a distinct
 * "presence" context, not confused with the amber grace/outgoing banners.
 *
 * Mount this wherever OutgoingShiftBanner is mounted (the app shell, below
 * the header). It self-hides for CNAs and while loading, so it's safe to
 * always render. Requires CheckinProvider above it.
 */
import { useState } from 'react';
import useCheckin from '../../hooks/useCheckin';

const TEAL_BG     = '#E6F0F4';
const TEAL_BORDER = '#1B6B93';
const TEAL_TEXT   = '#0F4A66';

const btnStyle = {
  background: '#fff',
  border: `1px solid ${TEAL_BORDER}`,
  color: TEAL_TEXT,
  padding: '6px 14px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  fontFamily: 'inherit',
};

const selectStyle = {
  padding: '6px 10px',
  borderRadius: 6,
  border: `1px solid ${TEAL_BORDER}`,
  background: '#fff',
  color: TEAL_TEXT,
  fontSize: 12,
  fontFamily: 'inherit',
};

export default function CheckinBanner() {
  const { checkedIn, facilities, usesCheckin, loading, checkIn, checkOut } = useCheckin();
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);

  // Only the float/oversight cohort checks in; nothing to show otherwise.
  if (!usesCheckin || loading) return null;

  const container = {
    background: TEAL_BG,
    borderBottom: `1px solid ${TEAL_BORDER}`,
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    fontSize: 13,
    fontWeight: 600,
    color: TEAL_TEXT,
  };

  const doCheckIn = async (facilityId) => {
    if (!facilityId) return;
    setBusy(true);
    try { await checkIn(Number(facilityId)); } finally { setBusy(false); }
  };

  const doCheckOut = async () => {
    setBusy(true);
    try { await checkOut(); } finally { setBusy(false); }
  };

  if (checkedIn) {
    return (
      <div style={container}>
        <span>📍 Checked in: <strong>{checkedIn.facility_name}</strong></span>
        <button onClick={doCheckOut} disabled={busy} style={btnStyle}>Check out</button>
      </div>
    );
  }

  // Not checked in.
  const single = facilities.length === 1 ? facilities[0] : null;

  return (
    <div style={container}>
      <span>📍 You&apos;re not checked in to a facility</span>
      {single ? (
        <button onClick={() => doCheckIn(single.facility_id)} disabled={busy} style={btnStyle}>
          Check in to {single.name}
        </button>
      ) : facilities.length > 1 ? (
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            aria-label="Select facility to check into"
            value={selected}
            onChange={e => setSelected(e.target.value)}
            style={selectStyle}
          >
            <option value="">Select facility…</option>
            {facilities.map(f => (
              <option key={f.facility_id} value={f.facility_id}>{f.name}</option>
            ))}
          </select>
          <button onClick={() => doCheckIn(selected)} disabled={busy || !selected} style={btnStyle}>
            Check in
          </button>
        </span>
      ) : (
        <span style={{ fontWeight: 400 }}>No facilities available</span>
      )}
    </div>
  );
}